import { useEffect, useState } from "react";
import { format } from "date-fns";
import { Button, CheckboxInput, Loading, Modal, Spinner, Typography } from "@/components/atoms";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useProfile } from "@/hooks";
import { useUpdateVehicleMutation } from "@/redux/apis/vehicle/vehicleApi";
import { VehicleDocumentType } from "@/redux/slices/vehicle/types";
import { addToast } from "@/utils";
import { VehicleDocumentUploadField } from "../../common/VehicleDocumentUploadField";
import { VehicleDocumentDateInput } from "../common/VehicleDocumentDateInput";
import { useVehicleDocuments } from "../../common/useVehicleDocuments";
import { useEditVehicleContext } from "../context";

interface UploadDocumentModalProps {
  vehicleId: string;
  documentType: VehicleDocumentType;
  open: boolean;
  onClose: () => void;
}

const LABELS: Record<string, { title: string; description: string; label: string }> = {
  INSURANCE: {
    title: "Upload Updated Insurance Document",
    description: "Please upload your latest vehicle insurance file",
    label: "New Vehicle Insurance Document",
  },
  ACCREDITATION: {
    title: "Upload Updated Accreditation Document",
    description: "Please upload your latest vehicle accreditation file",
    label: "New Vehicle Accreditation Document",
  },
};

export const UploadDocumentModal = ({ vehicleId, documentType, open, onClose }: UploadDocumentModalProps) => {
  const { dateFormat } = useProfile();
  const { vehicle } = useEditVehicleContext();
  const [updateVehicle, { isLoading: isSaving }] = useUpdateVehicleMutation();

  const [docId, setDocId] = useState<string | null>(null);
  const [expiryDate, setExpiryDate] = useState<Date | null>(null);
  const [expiryConfirm, setExpiryConfirm] = useState(false);
  const [retryCount, setRetryCount] = useState({ insurance: 0, accreditation: 0 });

  const documents = useVehicleDocuments({
    vehicleId,
    insuranceId: documentType === "INSURANCE" ? docId ?? undefined : undefined,
    accreditationId: documentType === "ACCREDITATION" ? docId ?? undefined : undefined,
    documentRetryCount: retryCount,
    setDocumentRetryCount: setRetryCount,
  });

  const docState = documentType === "INSURANCE" ? documents.insurance : documents.accreditation;
  const autoVerified = !docState.manualExpiryDate && !!docState.document?.expiryDate;

  // Sync auto-verified expiry date
  useEffect(() => {
    if (autoVerified && docState.document?.expiryDate) {
      setExpiryDate(new Date(docState.document.expiryDate));
      setExpiryConfirm(true);
    }
  }, [autoVerified, docState.document?.expiryDate]);

  const labels = LABELS[documentType] ?? LABELS.INSURANCE;

  const handleClose = () => {
    setDocId(null);
    setExpiryDate(null);
    setExpiryConfirm(false);
    setRetryCount({ insurance: 0, accreditation: 0 });
    onClose();
  };

  const isExpiryInPast = expiryDate ? expiryDate < new Date(new Date().setHours(0, 0, 0, 0)) : false;
  const canSubmit = docId && (autoVerified || (expiryDate && expiryConfirm && !isExpiryInPast)) && !documents.isProcessing && !isSaving;

  const handleSubmit = async () => {
    if (!docId || !expiryDate) return;

    try {
      const docPayload = vehicle.documents.map((d) => ({
        uuid: d.uuid,
        expiry_date: d.expiryDate,
        document_type: d.documentType as "INSURANCE" | "ACCREDITATION" | "REGISTRATION" | "OTHER",
      }));

      // Add or replace the uploaded document
      const existingIdx = docPayload.findIndex((d) => d.document_type === documentType);
      const newDoc = {
        uuid: docId,
        expiry_date: format(expiryDate, "yyyy-MM-dd"),
        document_type: documentType as "INSURANCE" | "ACCREDITATION" | "REGISTRATION" | "OTHER",
      };

      if (existingIdx >= 0) {
        docPayload[existingIdx] = newDoc;
      } else {
        docPayload.push(newDoc);
      }

      await updateVehicle({
        id: vehicleId,
        data: {
          identifier: vehicle.identifier,
          capacity_pax: vehicle.capacity.pax,
          capacity_bags: vehicle.capacity.bags,
          maintenance_fee: vehicle.maintenanceFee,
          documents: docPayload,
          categories: vehicle.categories.map((c) => ({ uuid: c.uuid })),
        },
      }).unwrap();

      addToast("success", `${documentType === "INSURANCE" ? "Insurance" : "Accreditation"} document uploaded successfully`);
      handleClose();
    } catch (e) {
      getErrorMessages(e as Parameters<typeof getErrorMessages>[0]).forEach((m) => addToast("danger", m));
    }
  };

  return (
    <Modal open={open} onClose={handleClose} className="w-[85vw] !p-6 md:w-[550px] lg:w-[550px]">
      <div className="relative flex flex-col">
        {isSaving && <Loading />}

        <Typography variant="title">{labels.title}</Typography>
        <Typography variant="paragraph" className="mt-1 text-neutral-dark-gray">
          {labels.description}
        </Typography>

        <div className="mt-5">
          <Typography variant="action" className="text-neutral-dark-gray">
            {labels.label}
          </Typography>
          <div className="mt-2">
            <VehicleDocumentUploadField
              vehicleId={vehicleId}
              document={docState.document}
              documentType={documentType}
              retryCount={documentType === "INSURANCE" ? retryCount.insurance : retryCount.accreditation}
              onUpload={(uuid) => {
                setDocId(uuid);
                setRetryCount((prev) => ({
                  ...prev,
                  [documentType === "INSURANCE" ? "insurance" : "accreditation"]: 0,
                }));
                setExpiryDate(null);
                setExpiryConfirm(false);
              }}
              onRemove={() => {
                setDocId(null);
                setExpiryDate(null);
                setExpiryConfirm(false);
              }}
              hasError={false}
            />
          </div>
          {docState.errorMessage && (
            <Typography className="mt-2 text-sm text-danger">{docState.errorMessage}</Typography>
          )}
        </div>

        {docState.document && (
          <div className="mt-4">
            <Typography variant="action" className="text-neutral-dark-gray">
              {documentType === "INSURANCE" ? "Insurance" : "Accreditation"} Expiry Date
            </Typography>
            <VehicleDocumentDateInput
              className="mt-2 w-full"
              selected={autoVerified && docState.document?.expiryDate ? new Date(docState.document.expiryDate) : expiryDate}
              onChange={(date) => {
                setExpiryDate(date as Date);
                setExpiryConfirm(false);
              }}
              placeholderText="-- / -- / ----"
              dateFormat={dateFormat}
              minDate={new Date()}
              disabled={docState.isLoading || !docState.manualExpiryDate}
            />
            {docState.manualExpiryDate && !docState.isLoading && (
              <div className="mt-2">
                <CheckboxInput
                  id={`confirm-${documentType.toLowerCase()}`}
                  label="I confirm that I am legally responsible for the date entered"
                  className="text-neutral-dark-gray [&_.typography]:!text-sm"
                  variant="check"
                  checked={expiryConfirm}
                  onChange={(e) => setExpiryConfirm(e.target.checked)}
                />
              </div>
            )}
          </div>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <Button variant="secondary" size="md" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" size="md" onClick={handleSubmit} disabled={!canSubmit}>
            {isSaving ? <Spinner /> : "Upload Document"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
